import InfoIcon from '@mui/icons-material/Info'
import DownloadIcon from '@mui/icons-material/Download'
import CheckIcon from '@mui/icons-material/Check'
import UndoIcon from '@mui/icons-material/Undo'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import CustomButton from '../../global/CustomButton'
import { Loading } from '../../global/Loading'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useLocalState } from '../../../utils/hooks/global/useLocalStorage'
import { colorGrid } from '../../../theme'

const STATUS_STYLES = {
  done:                  colorGrid.green,
  running:               colorGrid.yellow,
  failed:                colorGrid.red,
  awaiting_confirmation: colorGrid.blue,
  reverted:              colorGrid.gray
}

const STATUS_ICONS = {
  done: CheckCircleOutlineIcon,
  running: HourglassEmptyIcon,
  failed: ErrorOutlineIcon,
  awaiting_confirmation: NewReleasesIcon,
  reverted: HourglassEmptyIcon
}

// Telegram usernames: 5-32 letters, digits or underscores, starting with a
// letter
const TELEGRAM_HANDLE = /^[A-Za-z][A-Za-z0-9_]{4,31}$/
const normalizeHandle = (text) => text.trim().replace(/^@/, '')
// Picked users are profiles; typed handles are plain strings
const handleOf = (value) =>
  normalizeHandle(typeof value === 'string' ? value : value.telegramUserId)

function AdminManageChannels({ channel, profiles, channels = [] }) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const isConfirmed = confirmText === 'MIGRATE'
  const [isMigrating, setIsMigrating] = useState(false)
  const [activeReportId, setActiveReportId] = useState(null)
  const [pastReports, setPastReports] = useState([])
  const [loadingReports, setLoadingReports] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)
  const [selectedProfiles, setSelectedProfiles] = useState([])
  const [startFromChannel, setStartFromChannel] = useState(null)
  const [memberAction, setMemberAction] = useState(null) // 'invite' | 'promote' | 'kick'
  const [memberActionGroup, setMemberActionGroup] = useState(null)
  const [memberActionError, setMemberActionError] = useState(null)
  const [memberActionNotice, setMemberActionNotice] = useState(null)
  const [handleError, setHandleError] = useState(null)
  const [archiveUsers, setArchiveUsers] = useState(false)
  const [confirmingId, setConfirmingId] = useState(null)
  const [revertingId, setRevertingId] = useState(null)
  const [cancelingMemberAction, setCancelingMemberAction] = useState(false)
  const pollingRef = useRef(null)
  const memberActionPollingRef = useRef(null)

  const [jwt] = useLocalState(null, 'jwt')
  const backendUrl = process.env.REACT_APP_BACKEND_URL

  const authHeaders = {
    Accept: 'application/json',
    Authorization: 'Bearer ' + jwt
  }

  const telegramChannelOptions = useMemo(
    () =>
      channels.filter(
        (channelOption) =>
          channelOption?.channelType === 'telegram' && !!channelOption?.chatId
      ),
    [channels]
  )

  useEffect(() => {
    if (channel) {
      setStartFromChannel(null)
    }
  }, [channel])

  // Initial load of past reports
  useEffect(() => {
    if (channel) return
    setLoadingReports(true)
    fetch(`${backendUrl}/channels/migrations-reports/reports`, {
      credentials: 'include',
      headers: authHeaders
    })
      .then((r) => r.json())
      .then((data) => {
        const reports = Array.isArray(data) ? data : []
        setPastReports(reports)
        setLoadingReports(false)
        // Resume polling if a migration is still running (e.g. after page reload)
        const running = reports.find((r) => r.status === 'running')
        if (running) setActiveReportId(running.reportId)
      })
      .catch(() => setLoadingReports(false))
  }, [jwt, backendUrl, channel]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active report every 5 seconds until it finishes
  useEffect(() => {
    if (!activeReportId) return

    const poll = () => {
      fetch(
        `${backendUrl}/channels/migrations-reports/reports/${activeReportId}/summary`,
        { credentials: 'include', headers: authHeaders }
      )
        .then((r) => r.json())
        .then((summary) => {
          setPastReports((prev) => {
            const exists = prev.some((r) => r.reportId === summary.reportId)
            if (exists) {
              return prev.map((r) =>
                r.reportId === summary.reportId ? summary : r
              )
            }
            return [summary, ...prev]
          })
          if (
            summary.status === 'done' ||
            summary.status === 'failed' ||
            summary.status === 'awaiting_confirmation' ||
            summary.status === 'reverted'
          ) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
            setActiveReportId(null)
          }
        })
        .catch(() => {})
    }

    poll()
    pollingRef.current = setInterval(poll, 5000)

    return () => {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [activeReportId, jwt, backendUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll member-action job until done
  useEffect(() => {
    if (
      !memberActionGroup?.groupId ||
      memberActionGroup.status === 'done' ||
      memberActionGroup.status === 'cancelled'
    ) {
      return
    }

    const poll = () => {
      fetch(`${backendUrl}/channels/members/jobs/${memberActionGroup.groupId}`, {
        credentials: 'include',
        headers: authHeaders
      })
        .then((r) => r.json())
        .then((data) => {
          setMemberActionGroup(data)
          if (data.status === 'done' || data.status === 'cancelled') {
            clearInterval(memberActionPollingRef.current)
            memberActionPollingRef.current = null
          }
        })
        .catch(() => {})
    }

    poll()
    memberActionPollingRef.current = setInterval(poll, 3000)

    return () => {
      clearInterval(memberActionPollingRef.current)
      memberActionPollingRef.current = null
    }
  }, [memberActionGroup?.groupId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMigrateChannels = () => {
    setIsMigrating(true)
    fetch(`${backendUrl}/channels/migrations-reports/migrate`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(
            errorData.message || 'There was an error processing your request.'
          )
        }
        return response.json()
      })
      .then((data) => {
        setActiveReportId(data.reportId)
        setIsMigrating(false)
      })
      .catch((error) => {
        console.error('Migration error:', error.message)
        setIsMigrating(false)
      })
  }

  const handleDownloadReport = async (reportId) => {
    setDownloadingId(reportId)
    try {
      const response = await fetch(
        `${backendUrl}/channels/migrations-reports/reports/${reportId}/download`,
        { credentials: 'include', headers: { Authorization: 'Bearer ' + jwt } }
      )
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `migration-report-${reportId}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Download error', e)
    } finally {
      setDownloadingId(null)
    }
  }

  const handleConfirm = (reportId) => {
    setConfirmingId(reportId)
    fetch(`${backendUrl}/channels/migrations-reports/reports/${reportId}/confirm`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders
    })
      .then((r) => { if (!r.ok) throw new Error('Confirm failed') })
      .then(() => {
        // Resume polling — confirm runs async on the backend
        setActiveReportId(reportId)
      })
      .catch((e) => console.error('Confirm error:', e.message))
      .finally(() => setConfirmingId(null))
  }

  const handleRevert = (reportId) => {
    setRevertingId(reportId)
    fetch(`${backendUrl}/channels/migrations-reports/reports/${reportId}/revert`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders
    })
      .then(async (r) => {
        if (!r.ok) throw new Error('Revert failed')
        return r.json()
      })
      .then(() => {
        setPastReports((prev) =>
          prev.map((r) =>
            r.reportId === reportId ? { ...r, status: 'reverted' } : r
          )
        )
      })
      .catch((e) => console.error('Revert error:', e.message))
      .finally(() => setRevertingId(null))
  }

  // Adds typed handles (several can be pasted, separated by spaces or
  // commas) next to picked users, skipping duplicates and invalid ones
  const handleUsersChange = (values) => {
    const next = []
    const invalid = []
    for (const value of values) {
      const items =
        typeof value === 'string'
          ? value.split(/[\s,]+/).map(normalizeHandle).filter(Boolean)
          : [value]
      for (const item of items) {
        if (typeof item === 'string' && !TELEGRAM_HANDLE.test(item)) {
          invalid.push(item)
          continue
        }
        const handle = handleOf(item).toLowerCase()
        if (!next.some((existing) => handleOf(existing).toLowerCase() === handle)) {
          next.push(item)
        }
      }
    }
    setHandleError(
      invalid.length
        ? `Not a Telegram handle: ${invalid.join(', ')}`
        : null
    )
    setSelectedProfiles(next)
  }

  const handleMemberAction = (action) => {
    if (selectedProfiles.length === 0) return
    setMemberAction(action)
    setMemberActionGroup(null)
    setMemberActionError(null)
    setMemberActionNotice(null)
    const telegramUsernames = selectedProfiles.map(handleOf)
    const payload = { telegramUsernames }
    if (!channel && startFromChannel?.id) {
      payload.startFromChannelId = startFromChannel.id
    }
    if (!channel && action === 'kick' && archiveUsers) {
      payload.archiveUsers = true
    }
    const url = channel
      ? `${backendUrl}/channels/${channel.id}/members/${action}`
      : `${backendUrl}/channels/members/${action}`
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (res.status === 409 && body?.groupId) {
            setMemberActionGroup({
              groupId: body.groupId,
              status: body.status,
              action: body.action,
              ok: body.ok ?? 0,
              failed: body.failed ?? 0,
              skipped: body.skipped ?? 0,
              total: body.total ?? 0,
              resumeAt: body.resumeAt,
              cancelRequested: body.cancelRequested
            })
            return null
          }
          throw new Error(body.error || body.message || 'Request failed')
        }
        const data = body
        if (data?.groupId) {
          setMemberActionGroup(data)
        } else {
          setMemberActionNotice(
            `${action === 'invite' ? 'Invited' : action === 'promote' ? 'Promoted' : 'Kicked'} ${telegramUsernames.map((handle) => `@${handle}`).join(', ')}`
          )
        }
        if (data?.archivedUsers?.length) {
          setMemberActionNotice(
            `Archived in the app: ${data.archivedUsers.join(', ')}`
          )
        }
        return data
      })
      .catch((e) => {
        console.error(`${action} error:`, e.message)
        setMemberActionError(e.message)
      })
      .finally(() => setMemberAction(null))
  }

  const handleCancelMemberAction = () => {
    if (!memberActionGroup?.groupId) return
    setCancelingMemberAction(true)
    fetch(
      `${backendUrl}/channels/members/jobs/${memberActionGroup.groupId}/cancel`,
      {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      }
    )
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || 'Cancel failed')
        }
        return res.json()
      })
      .then((data) => setMemberActionGroup(data))
      .catch((e) => console.error('cancel member action error:', e.message))
      .finally(() => setCancelingMemberAction(false))
  }

  return (
    <form>
      <Stack
        direction="column"
        width="100%"
        spacing={2}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {!channel && (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              color: 'gray',
              alignItems: 'center'
            }}
          >
            <InfoIcon />
            <Typography variant="body1">
              All actions will be performed on all existing channels!
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            color: 'text.disabled',
            alignItems: 'center'
          }}
        >
          <InfoIcon fontSize="small" />
          <Typography variant="body2">
            Users must allow <b>@cf_admin_account</b> to add them to groups in Telegram privacy settings
          </Typography>
        </Box>

        {!channel && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              width: '100%',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: '8px !important',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Action options
                </Typography>
                {startFromChannel && (
                  <Chip
                    size="small"
                    label={`Start from: ${startFromChannel.name}`}
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth>
                <Autocomplete
                  name="startFromChannelId"
                  options={telegramChannelOptions}
                  getOptionLabel={(option) => `${option.name} (${option.chatId})`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={startFromChannel}
                  onChange={(_, value) => setStartFromChannel(value)}
                  disabled={
                    !!memberAction ||
                    ['running', 'paused', 'cancelling'].includes(
                      memberActionGroup?.status
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Start from channel"
                      placeholder="Optional"
                    />
                  )}
                />
              </FormControl>
            </AccordionDetails>
          </Accordion>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <FormControl>
            <Autocomplete
              name="profileIds"
              sx={{ width: { xs: '100%', sm: '320px' } }}
              multiple
              freeSolo
              id="tags-outlined"
              options={profiles.filter(
                (profile) => profile.telegramUserId && !profile.user?.archivedAt
              )}
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? `@${option}`
                  : `${option.user.displayName} (@${handleOf(option)})`
              }
              filterSelectedOptions
              value={selectedProfiles}
              onChange={(_, value) => handleUsersChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Users or Telegram handles"
                  placeholder="Pick a user or type @handle + Enter"
                  error={!!handleError}
                  helperText={
                    handleError ??
                    'Handles work for people who are not in the app'
                  }
                />
              )}
            />
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <CustomButton
              color="black"
              icon={AddIcon}
              sx={{ width: '120px' }}
              buttonText={memberAction === 'invite' ? 'Inviting…' : 'Invite'}
              disabled={
                selectedProfiles.length === 0 ||
                !!memberAction ||
                ['running', 'paused', 'cancelling'].includes(memberActionGroup?.status)
              }
              onClick={() => handleMemberAction('invite')}
            />
            <CustomButton
              color="blackOutlined"
              icon={KeyboardArrowUpIcon}
              sx={{ width: '120px' }}
              buttonText={memberAction === 'promote' ? 'Promoting…' : 'Promote'}
              disabled={
                selectedProfiles.length === 0 ||
                !!memberAction ||
                ['running', 'paused', 'cancelling'].includes(memberActionGroup?.status)
              }
              onClick={() => handleMemberAction('promote')}
            />
            <Divider sx={{ my: '6px', borderColor: 'rgba(0, 0, 0, 0.08)' }} />
            <CustomButton
              color="red"
              icon={RemoveIcon}
              sx={{ width: '120px' }}
              buttonText={memberAction === 'kick' ? 'Kicking…' : 'Kick'}
              disabled={
                selectedProfiles.length === 0 ||
                !!memberAction ||
                ['running', 'paused', 'cancelling'].includes(memberActionGroup?.status)
              }
              onClick={() => handleMemberAction('kick')}
            />
          </Box>
        </Box>

        {!channel && (
          <FormControlLabel
            control={
              <Checkbox
                checked={archiveUsers}
                onChange={(e) => setArchiveUsers(e.target.checked)}
              />
            }
            label="When kicking, also archive their accounts in the app"
          />
        )}
        {memberActionError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {memberActionError}
          </Alert>
        )}
        {memberActionNotice && (
          <Alert severity="success" sx={{ width: '100%' }}>
            {memberActionNotice}
          </Alert>
        )}

        {memberActionGroup && (() => {
          const status = memberActionGroup.status
          const isRunning = status === 'running'
          const isPaused = status === 'paused'
          const isCancelling = status === 'cancelling'
          const isCancelled = status === 'cancelled'
          const isActive = isRunning || isPaused || isCancelling
          const hasFailed = memberActionGroup.failed > 0
          let StatusIcon = CheckCircleOutlineIcon
          if (isRunning || isPaused || isCancelling) {
            StatusIcon = HourglassEmptyIcon
          } else if (isCancelled || hasFailed) {
            StatusIcon = ErrorOutlineIcon
          }

          let color = 'success.main'
          if (isRunning || isPaused || isCancelling) {
            color = 'text.secondary'
          } else if (isCancelled || hasFailed) {
            color = 'error.main'
          }
          const doneCount = memberActionGroup.ok + memberActionGroup.failed
          return (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', color }}>
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <StatusIcon fontSize="small" />
                <Typography variant="body2" color="inherit">
                  {isRunning && (
                    <>
                      <b>{memberActionGroup.action}</b>:{' '}
                      {doneCount}/{memberActionGroup.total} channels done
                      {hasFailed && <> · <b>{memberActionGroup.failed} failed</b></>}
                      {' '}— running…
                    </>
                  )}
                  {isPaused && (
                    <>
                      <b>{memberActionGroup.action}</b>:{' '}
                      {doneCount}/{memberActionGroup.total} channels done
                      {hasFailed && <> · <b>{memberActionGroup.failed} failed</b></>}
                      {' '}— paused by flood wait
                      {memberActionGroup.resumeAt
                        ? `, retry at ${new Date(memberActionGroup.resumeAt).toLocaleString()}`
                        : ''}
                    </>
                  )}
                  {isCancelling && (
                    <>
                      <b>{memberActionGroup.action}</b>:{' '}
                      {doneCount}/{memberActionGroup.total} channels done — cancelling…
                    </>
                  )}
                  {isCancelled && (
                    <>
                      <b>{memberActionGroup.action}</b> cancelled:{' '}
                      {memberActionGroup.ok} ok
                      {hasFailed && <>, <b>{memberActionGroup.failed} failed</b></>}
                      {memberActionGroup.skipped > 0 && `, ${memberActionGroup.skipped} skipped`}
                    </>
                  )}
                  {!isActive && !isCancelled && (
                    <>
                      <b>{memberActionGroup.action}</b> complete:{' '}
                      {memberActionGroup.ok} ok
                      {hasFailed && <>, <b>{memberActionGroup.failed} failed</b></>}
                      {memberActionGroup.skipped > 0 && `, ${memberActionGroup.skipped} skipped`}
                    </>
                  )}
                </Typography>
              </Box>
              {isActive && (
                <CustomButton
                  color="red"
                  icon={StopCircleIcon}
                  sx={{ width: '120px' }}
                  buttonText={cancelingMemberAction ? 'Cancelling…' : 'Cancel'}
                  disabled={cancelingMemberAction}
                  onClick={handleCancelMemberAction}
                />
              )}
            </Box>
          )
        })()}

        {!channel && (
          <>
            <Divider
              sx={{ my: '6px', width: '100%', borderColor: 'rgba(0, 0, 0, 0.08)' }}
            />

            {/* Migration — collapsed by default since it's rarely needed */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                width: '100%',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: '8px !important',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NewReleasesIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  <Typography variant="body2" fontWeight={600}>
                    Telegram Channel Migration
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      color: 'text.secondary',
                      alignItems: 'center'
                    }}
                  >
                    <InfoIcon fontSize="small" />
                    <Typography variant="body2">
                      Only perform this action if you know what you are doing!
                    </Typography>
                  </Box>

                  {showConfirm ? (
                    <Stack spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label='Type "MIGRATE" to confirm'
                        value={confirmText}
                        onChange={(e) =>
                          setConfirmText(e.target.value.toUpperCase())
                        }
                        error={confirmText.length > 0 && !isConfirmed}
                        helperText={
                          confirmText.length > 0 && !isConfirmed
                            ? 'Confirmation text does not match'
                            : ' '
                        }
                        sx={{ width: '260px' }}
                        onPaste={(e) => e.preventDefault()}
                      />
                      <CustomButton
                        color="red"
                        icon={NewReleasesIcon}
                        sx={{ width: '170px' }}
                        buttonText={isMigrating ? 'Starting…' : 'Confirm Migration'}
                        disabled={!isConfirmed || isMigrating}
                        onClick={() => {
                          handleMigrateChannels()
                          setConfirmText('')
                          setShowConfirm(false)
                        }}
                      />
                    </Stack>
                  ) : (
                    <CustomButton
                      color="blackOutlined"
                      icon={NewReleasesIcon}
                      sx={{ width: '150px' }}
                      buttonText="Migrate Channels"
                      onClick={() => setShowConfirm(true)}
                    />
                  )}

                  <Divider sx={{ width: '100%', borderColor: 'rgba(0,0,0,0.08)' }} />

                  <Typography
                    variant="subtitle2"
                    sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
                  >
                    Reports
                  </Typography>

                  {loadingReports ? (
                    <Loading size={24} thickness={5} />
                  ) : pastReports.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      No reports yet
                    </Typography>
                  ) : (
                    <List
                      dense
                      disablePadding
                      sx={{ width: '100%', maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {pastReports.map((r) => {
                        const StatusIcon =
                          STATUS_ICONS[r.status] ?? HourglassEmptyIcon
                        return (
                          <ListItem
                            key={r.reportId}
                            disablePadding
                            divider
                            secondaryAction={
                              <Tooltip title="Download CSV">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      r.status === 'running' ||
                                      r.status === 'awaiting_confirmation' ||
                                      downloadingId === r.reportId
                                    }
                                    onClick={() =>
                                      handleDownloadReport(r.reportId)
                                    }
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            }
                          >
                            <ListItemButton disableRipple sx={{ cursor: 'default', pr: '48px' }}>
                              <ListItemText
                                disableTypography
                                primary={
                                  <Stack direction="column" spacing={0.5}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Chip
                                        size="small"
                                        label={r.status.replace('_', ' ')}
                                        icon={<StatusIcon />}
                                        sx={{
                                          backgroundColor: (STATUS_STYLES[r.status] ?? colorGrid.gray).background,
                                          color: (STATUS_STYLES[r.status] ?? colorGrid.gray).text,
                                          fontWeight: 600,
                                          '& .MuiChip-icon': {
                                            color: (STATUS_STYLES[r.status] ?? colorGrid.gray).text
                                          }
                                        }}
                                      />
                                      <Typography variant="caption">
                                        {r.processed}/{r.total} processed &nbsp;·&nbsp;{' '}
                                        {r.fulfilled} ok &nbsp;·&nbsp; {r.partial} partial
                                        &nbsp;·&nbsp; {r.rejected} failed
                                      </Typography>
                                    </Stack>
                                    {r.status === 'running' && r.pending > 0 && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {r.pending} queued
                                        {r.delayed > 0 && r.nextJobAt
                                          ? ` · flood wait — next retry at ${new Date(r.nextJobAt).toLocaleTimeString()}`
                                          : ''}
                                      </Typography>
                                    )}
                                    {r.status === 'awaiting_confirmation' && (
                                      <Stack direction="row" spacing={1}>
                                        <CustomButton
                                          icon={CheckIcon}
                                          sx={{
                                            backgroundColor: colorGrid.green.background,
                                            color: colorGrid.green.text,
                                            padding: '3px 10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.28)' },
                                            '&:disabled': { opacity: 0.5 }
                                          }}
                                          buttonText={confirmingId === r.reportId ? 'Confirming…' : 'Confirm'}
                                          disabled={!!confirmingId || !!revertingId}
                                          onClick={() => handleConfirm(r.reportId)}
                                        />
                                        <CustomButton
                                          icon={UndoIcon}
                                          sx={{
                                            backgroundColor: colorGrid.red.background,
                                            color: colorGrid.red.text,
                                            padding: '3px 10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            '&:hover': { backgroundColor: 'rgba(255, 86, 48, 0.28)' },
                                            '&:disabled': { opacity: 0.5 }
                                          }}
                                          buttonText={revertingId === r.reportId ? 'Reverting…' : 'Revert'}
                                          disabled={!!confirmingId || !!revertingId}
                                          onClick={() => handleRevert(r.reportId)}
                                        />
                                      </Stack>
                                    )}
                                  </Stack>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', mt: '2px', display: 'block' }}
                                  >
                                    {new Date(r.startedAt).toLocaleString()}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        )
                      })}
                    </List>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
    </form>
  )
}

export default AdminManageChannels
